import { useState } from "react"
import { CheckCheck, Clock, UserRoundPlus } from "lucide-react"
import { api } from "@/lib/api"
import { toast } from "@/components/portal/Toast"
import { UebergabeDialog } from "./UebergabeDialog"
import { naechsterFreitag, tagInTagen, tagKurz, useUebergeben, useZusammenarbeitNeuLaden } from "./zusammenarbeit"

export type OffenerPunkt = { key: string; label: string }

/** Punkte, die sagen, was schon geschehen ist (Nachschau, Plan-Ausgang #34,
 * Termin vorbei #35) — keine Aufgabe, sondern die Frage „erledigt?". */
export const HINWEIS_PUNKTE = ["danach", "ausgang", "termin_war"]

/**
 * „Noch offen" unter dem Kopf der Mail.
 *
 * 🔑 Alles, was jemand an dieser Mail noch TUN muss, steht hier — nicht in
 * einem Reiter der rechten Spalte. Die Reiter sind zum Nachschlagen da; was
 * dort versteckt wäre, ginge bei einer Übergabe genau so verloren wie vorher.
 *
 * Die Punkte kommen vom Backend (services/mail_offen.offene_punkte) — dieselbe
 * Rechnung, mit der die Plan-Karte abhakt.
 */
export function NochOffenLeiste({ emailId, punkte, status, zustaendig, wiedervorlageAm, onPunkt }: {
  emailId: number
  punkte: OffenerPunkt[]
  status?: string | null
  zustaendig?: string | null
  wiedervorlageAm?: string | null
  /** Klick auf einen Punkt: zur passenden Stelle (Plan, Notizen …). */
  onPunkt?: (key: string) => void
}) {
  const [uebergabe, setUebergabe] = useState(false)
  const [wvOffen, setWvOffen] = useState(false)
  const neu = useZusammenarbeitNeuLaden(emailId)
  const uebergeben = useUebergeben(emailId)
  const erledigt = status === "done"
  if (erledigt && punkte.length === 0) return null
  if (punkte.length === 0 && !wiedervorlageAm) return null

  const alsErledigt = async () => {
    const { error } = await api.PATCH("/api/v1/emails/{email_id}", {
      params: { path: { email_id: emailId } }, body: { status: "done" },
    })
    if (error) { toast("Konnte nicht als erledigt markieren", "error"); return }
    toast("Als erledigt markiert", "success")
    neu()
  }
  const wiedervorlage = (am: string | null) => {
    setWvOffen(false)
    uebergeben.mutate({ zustaendig: zustaendig ?? null, wiedervorlage_am: am, wiedervorlage_loeschen: am === null }, {
      onSuccess: () => toast(am ? `Wird am ${tagKurz(am)} wieder vorgelegt` : "Wiedervorlage entfernt", "success"),
      onError: () => toast("Wiedervorlage fehlgeschlagen", "error"),
    })
  }

  return (
    <div className="ev2-noch-offen" data-testid="noch-offen">
      <span className="ev2-noch-offen-titel"><Clock className="w-4 h-4" aria-hidden />
        {punkte.length > 0 && punkte.every((p) => p.key === "wartet") ? "Wartet" : "Noch offen"}</span>
      {punkte.map((p) => (
        <button key={p.key} type="button" className={HINWEIS_PUNKTE.includes(p.key) ? "ev2-offen-chip danach" : "ev2-offen-chip"} onClick={() => onPunkt?.(p.key)}
          title="Zur passenden Stelle springen">
          <span className="ev2-offen-kreis" aria-hidden />{p.label}
        </button>
      ))}
      {wiedervorlageAm && !punkte.some((p) => p.key === "wiedervorlage") && (
        <span className="ev2-offen-hinweis">Wiedervorlage {tagKurz(wiedervorlageAm)}</span>
      )}
      <span style={{ flex: 1 }} />
      <div style={{ position: "relative" }}>
        <button type="button" className="ev2-offen-knopf" onClick={() => setWvOffen(!wvOffen)} aria-expanded={wvOffen}>
          Wiedervorlage…
        </button>
        {wvOffen && (
          <div className="ev2-popover" role="menu">
            <button type="button" role="menuitem" onClick={() => wiedervorlage(tagInTagen(1))}>Morgen</button>
            <button type="button" role="menuitem" onClick={() => wiedervorlage(naechsterFreitag())}>Freitag</button>
            <button type="button" role="menuitem" onClick={() => wiedervorlage(tagInTagen(7))}>In einer Woche</button>
            {wiedervorlageAm && <button type="button" role="menuitem" onClick={() => wiedervorlage(null)}>Entfernen</button>}
          </div>
        )}
      </div>
      <button type="button" className="ev2-offen-knopf" onClick={() => setUebergabe(true)}>
        <UserRoundPlus className="w-3.5 h-3.5" aria-hidden /> Übergeben…
      </button>
      {!erledigt && (
        // #35: Termin vorbei → derselbe Knopf, aber er sagt, warum.
        <button type="button" className="ev2-offen-knopf" onClick={alsErledigt}>
          <CheckCheck className="w-3.5 h-3.5" aria-hidden />
          {punkte.some((p) => p.key === "termin_war") ? " Termin war – erledigen" : " Erledigt"}
        </button>
      )}
      {uebergabe && (
        <UebergabeDialog emailId={emailId} offen onClose={() => setUebergabe(false)} aktuell={zustaendig} />
      )}
    </div>
  )
}
